import { useMemo, useState } from 'react';
import { Header } from '@/components/layout/header';
import { LoadingState } from '@/components/loading-state';
import { ErrorState } from '@/components/error-state';
import { useApi } from '@/hooks/use-api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
} from 'recharts';
import {
  bandRows,
  type LatencyVsLoad,
  MIN_REQUESTS_PER_POINT,
  monthsOf,
  routesByVolume,
} from '@/lib/latency-vs-load';

interface RouteStat {
  route: string;
  requests: number;
  p50Ms: number;
  p95Ms: number;
  maxMs: number;
  errors5xx: number;
}
interface SlowQueryStat {
  service: string;
  calls: number;
  p95Ms: number;
  maxMs: number;
}
interface DailyVolume {
  day: string;
  requests: number;
  errors4xx: number;
  errors5xx: number;
}

const CHART_TOOLTIP_STYLE = {
  backgroundColor: 'var(--card)',
  border: '1px solid var(--border)',
  borderRadius: '8px',
  fontSize: '12px',
} as const;

const fmtMs = (ms: number) => (ms >= 10_000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`);

export function ApiMetricsPage() {
  const routes = useApi<RouteStat[]>('/api/api-metrics/routes');
  const slowQueries = useApi<SlowQueryStat[]>('/api/api-metrics/slow-queries');
  const daily = useApi<DailyVolume[]>('/api/api-metrics/daily');
  const latencyVsLoad = useApi<LatencyVsLoad>('/api/api-metrics/latency-vs-load');

  const all = [routes, slowQueries, daily, latencyVsLoad];
  const anyLoading = all.some((a) => a.loading);
  const errors = all.map((a) => a.error).filter((e): e is string => e !== null);
  const lastFetched = all
    .map((a) => a.lastFetched)
    .filter((d): d is Date => d !== null)
    .sort((a, b) => b.getTime() - a.getTime())[0];

  const refreshAll = () => {
    fetch('/api/cache/invalidate', { method: 'POST' })
      .catch(() => {})
      .finally(() => all.forEach((a) => a.refresh()));
  };

  return (
    <div className="flex flex-col">
      <Header
        title="API"
        lastFetched={lastFetched ?? null}
        onRefresh={refreshAll}
        loading={anyLoading}
      />
      <div className="space-y-6 p-6">
        {errors.length > 0 && <ErrorState message={errors.join(' · ')} onRetry={refreshAll} />}
        {anyLoading && !routes.data && <LoadingState rows={4} />}
        {latencyVsLoad.data && <LatencyVsLoadChart data={latencyVsLoad.data} />}
        {daily.data && <DailyVolumeChart days={daily.data} />}
        {routes.data && <RoutesTable rows={routes.data} />}
        {slowQueries.data && <SlowQueriesTable rows={slowQueries.data} />}
      </div>
    </div>
  );
}

// One colour per month of the window, newest last, so a month keeps its colour
// when the endpoint changes. The newest month is also drawn thicker.
const MONTH_COLORS = [
  'var(--muted-foreground)',
  'var(--chart-3)',
  'var(--chart-2)',
  'var(--chart-5)',
  'var(--chart-4)',
  'var(--chart-1)',
];

function LatencyVsLoadChart({ data }: { data: LatencyVsLoad }) {
  const [route, setRoute] = useState<string | null>(null);
  const routes = useMemo(() => routesByVolume(data.points), [data.points]);
  // The window's months, not just this endpoint's, so colours and the bold line stay put.
  const months = useMemo(() => monthsOf(data.points), [data.points]);
  const rows = useMemo(() => bandRows(data.points, route), [data.points, route]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="text-base">p95 response time vs load, by month</CardTitle>
          <p className="text-xs text-muted-foreground">
            x = requests to all endpoints in that UTC hour · gaps = under {MIN_REQUESTS_PER_POINT}{' '}
            requests · act if this month sits above last month at the same load
          </p>
        </div>
        <select
          aria-label="Endpoint"
          className="h-8 max-w-72 rounded-md border border-input bg-background px-2 font-mono text-xs"
          value={route ?? ''}
          onChange={(e) => setRoute(e.target.value || null)}
        >
          <option value="">All endpoints</option>
          {routes.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No requests since {data.from}.</p>
        ) : (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={rows}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="label" fontSize={11} />
                <YAxis fontSize={11} width={48} tickFormatter={fmtMs} />
                <Tooltip
                  contentStyle={CHART_TOOLTIP_STYLE}
                  labelFormatter={(label: string) => `${label} requests/hour`}
                  formatter={(value: number, month: string, item) => [
                    `${fmtMs(value)} (${item.payload[`${month}:n`]} requests)`,
                    month,
                  ]}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {months.map((m, i) => (
                  <Line
                    key={m}
                    type="monotone"
                    dataKey={m}
                    stroke={MONTH_COLORS.at(i - months.length)}
                    strokeWidth={i === months.length - 1 ? 2.5 : 1.5}
                    dot={{ r: 2 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DailyVolumeChart({ days }: { days: DailyVolume[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Requests per day (14d)</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={days}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="day" fontSize={11} tickFormatter={(d: string) => d.slice(5)} />
            <YAxis fontSize={11} />
            <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
            <Line type="monotone" dataKey="requests" stroke="var(--chart-1)" dot={false} />
            <Line type="monotone" dataKey="errors5xx" stroke="var(--destructive)" dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

function RoutesTable({ rows }: { rows: RouteStat[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Route latency, slowest p95 first (7d)</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Route</TableHead>
              <TableHead className="text-right">Requests</TableHead>
              <TableHead className="text-right">p50</TableHead>
              <TableHead className="text-right">p95</TableHead>
              <TableHead className="text-right">Max</TableHead>
              <TableHead className="text-right">5xx</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.route}>
                <TableCell className="font-mono text-xs">{r.route}</TableCell>
                <TableCell className="text-right">{r.requests}</TableCell>
                <TableCell className="text-right">{fmtMs(r.p50Ms)}</TableCell>
                <TableCell className="text-right">{fmtMs(r.p95Ms)}</TableCell>
                <TableCell className="text-right">{fmtMs(r.maxMs)}</TableCell>
                <TableCell className={`text-right ${r.errors5xx > 0 ? 'text-destructive' : ''}`}>
                  {r.errors5xx}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function SlowQueriesTable({ rows }: { rows: SlowQueryStat[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Slowest DB query services (7d, from [Query] traces)</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Service.method</TableHead>
              <TableHead className="text-right">Calls</TableHead>
              <TableHead className="text-right">p95</TableHead>
              <TableHead className="text-right">Max</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.service}>
                <TableCell className="font-mono text-xs">{r.service}</TableCell>
                <TableCell className="text-right">{r.calls}</TableCell>
                <TableCell className="text-right">{fmtMs(r.p95Ms)}</TableCell>
                <TableCell className="text-right">{fmtMs(r.maxMs)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
